import { getDocs, query, where } from 'firebase/firestore';
import { DailySales, dailySalesRef } from '@api/IncomeStatement/DailySales';
import { CashFlow, cashFlowsRef } from '@api/CashFlows/CashFlow';
import { inventoryOrdersRef, InventoryOrder, InventoryOrderStatus } from '@api/InventoryOrders/InventoryOrder';
import { CedisPurchase, dayKey, StatementRange } from '@utils/IncomeStatement';
import { toDateSafe } from '@utils/Date';

/** Sucursales visibles: `'all'` o una lista. El filtro va en el cliente (sin índices). */
export type BranchScope = 'all' | string[];

const inScope = (branchOfficeId: string | undefined, scope: BranchScope): boolean =>
    scope === 'all' || (!!branchOfficeId && scope.includes(branchOfficeId));

const startOfDay = (day: string): Date => new Date(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10)));
const nextDay = (day: string): Date => {
    const date = startOfDay(day);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
};

export interface IncomeStatementData {
    dailySales: DailySales[];
    flows: CashFlow[];
    cedisPurchases: CedisPurchase[];
}

/**
 * Lo que necesita el Estado de Resultados, en UNA pasada que cubre el periodo y el
 * anterior (se reparten en memoria). Lecturas por carga:
 * - `daily-sales`: ≤ 31 por sucursal y mes (≤ 310 un mes consolidado, ×2 con el anterior).
 * - `cash-flows` por fecha: ⚠️ la colección mezcla el efectivo de cada pedido (~90 %)
 *   con los gastos, así que son ~130 docs por día consolidado (~4 k un mes, ×2). No
 *   hay forma de pedir "solo gastos" por fecha sin índice compuesto. Solo se lee al
 *   abrir el ER o cambiar el periodo. Si llega a tardar: agregado de gastos por día.
 * - `cash-flows` por `period` (gastos del mes pagados en otro mes): un puñado.
 * - `inventory-orders` por `createdAt`: ~80 por mes consolidado.
 */
export default {
    async getIncomeStatementData(ranges: StatementRange[], scope: BranchScope): Promise<IncomeStatementData> {
        const valid = ranges.filter((range) => !range.empty);
        if (valid.length === 0) return { dailySales: [], flows: [], cedisPurchases: [] };
        const startDay = valid.map((range) => range.startDay).sort()[0];
        const endDay = valid.map((range) => range.endDay).sort().reverse()[0];
        const periods = Array.from(new Set(valid.flatMap((range) => range.periods))).slice(0, 30);
        const start = startOfDay(startDay);
        const end = nextDay(endDay);

        const [salesSnap, flowsSnap, periodSnap, cedisSnap] = await Promise.all([
            getDocs(query(dailySalesRef, where('day', '>=', startDay), where('day', '<=', endDay))),
            getDocs(query(cashFlowsRef, where('date', '>=', start), where('date', '<', end))),
            periods.length ? getDocs(query(cashFlowsRef, where('period', 'in', periods))) : Promise.resolve(undefined),
            getDocs(query(inventoryOrdersRef, where('createdAt', '>=', start), where('createdAt', '<', end)))
        ]);

        const flowsById = new Map<string, CashFlow>();
        [...flowsSnap.docs, ...(periodSnap?.docs || [])].forEach((document) => {
            const flow = { ...(document.data() as CashFlow), id: document.id };
            // El efectivo de los pedidos no es gasto: fuera desde aquí para no cargarlo en memoria.
            if (flow.orderId || !inScope(flow.branchOfficeId, scope)) return;
            flowsById.set(document.id, flow);
        });

        const cedisPurchases: CedisPurchase[] = cedisSnap.docs
            .map((document) => document.data() as InventoryOrder)
            .filter(
                (order) =>
                    order.paymentStatus === 'paid' &&
                    order.status !== InventoryOrderStatus.canceled &&
                    inScope(order.branchOfficeId, scope)
            )
            .map((order) => ({
                branchOfficeId: order.branchOfficeId,
                total: Number(order.total) || 0,
                day: (() => {
                    const date = toDateSafe(order.createdAt);
                    return date ? dayKey(date) : '';
                })()
            }));

        return {
            dailySales: salesSnap.docs.map((document) => document.data() as DailySales).filter((row) => inScope(row.branchOfficeId, scope)),
            flows: Array.from(flowsById.values()),
            cedisPurchases
        };
    }
};
